import React, { useState, useEffect } from "react";
import DragDrop from "../../DragDrop";
import { Trash } from "@phosphor-icons/react";
import axiosInstance from "../../../api/axiosInstance";
import ImageLoader from "../../loader/ImageLoader";
import PageLoader from "../../loader/PageLoader";
import { uploadImageToServer } from "../../../api/uploadImage";

const MediaGallery = ({ onComplete }) => {
    const [media, setMedia] = useState([]); // To store images and their statuses
    const [isPageLoading, setIsPageLoading] = useState(true);
    const hotelId = localStorage.getItem("hotelId");

    useEffect(() => {
        const fetchHotelPhotos = async () => {
            try {
                const response = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}`,
                );
                const existingPhotos = response.data.photos || [];
                setMedia(
                    existingPhotos.map((key) => ({
                        key,
                        url: "",
                        loading: true,
                        error: false,
                    })),
                );

                fetchFloorPlanImages(existingPhotos);
                setIsPageLoading(false);
            } catch (error) {
                console.error("Failed to fetch hotel photos:", error);
                setIsPageLoading(false);
            }
        };

        fetchHotelPhotos();
    }, [hotelId]);

    const fetchFloorPlanImages = async (imageKeys = []) => {
        setMedia(
            imageKeys.map((key) => ({
                key,
                url: "",
                loading: true,
                error: false,
            })),
        );

        const imagePromises = imageKeys.map(async (key) => {
            try {
                const response = await axiosInstance.get(
                    `/api/v1/images/download-presigned-url?key=${key}`,
                );
                const imageUrl = response.data.url;
                return { key, url: imageUrl, loading: false, error: false };
            } catch (error) {
                console.error("Error fetching image URL:", error);
                return { key, url: "", loading: false, error: true };
            }
        });

        const images = await Promise.all(imagePromises);
        setMedia(images);
    };

    const handleFileUpload = async (files) => {
        const fileArray = Array.from(files);
        const newMediaEntries = fileArray.map((file) => ({
            key: file.name,
            url: URL.createObjectURL(file),
            loading: true,
            error: false,
        }));
        setMedia((prevMedia) => [...prevMedia, ...newMediaEntries]);

        const uploadPromises = newMediaEntries.map(async (entry, index) => {
            try {
                const uploadUrlResponse = await axiosInstance.get(
                    `/api/v1/images/upload-presigned-url?file_name=${entry.key}`,
                );
                const { key, url } = uploadUrlResponse.data;
                const isSuccess = await uploadImageToServer(
                    fileArray[index],
                    url,
                );

                setMedia((prevMedia) =>
                    prevMedia.map((mediaEntry) =>
                        mediaEntry.key === entry.key
                            ? {
                                  ...mediaEntry,
                                  key: key, // Replace with actual key
                                  loading: false,
                                  error: !isSuccess,
                              }
                            : mediaEntry,
                    ),
                );
                return isSuccess;
            } catch (error) {
                console.error("Error uploading image:", error);
                setMedia((prevMedia) =>
                    prevMedia.map((mediaEntry) =>
                        mediaEntry.key === entry.key
                            ? { ...mediaEntry, loading: false, error: true }
                            : mediaEntry,
                    ),
                );
                return false;
            }
        });

        await Promise.all(uploadPromises);
    };

    const removeImage = (indexToRemove) => {
        setMedia((prevMedia) =>
            prevMedia.filter((_, index) => index !== indexToRemove),
        );
    };

    const trimImgName = (name) => {
        if (name && name.length > 12) {
            return name.substring(0, 12) + "...";
        }
        return name || "";
    };

    const handleSubmit = async () => {
        setIsPageLoading(true);
        try {
            const allImageKeys = media
                .filter((image) => !image.loading && !image.error)
                .map((image) => image.key);

            const response = await axiosInstance.patch(
                `/api/v1/hotels/${hotelId}/general-info`,
                { photos: allImageKeys },
            );

            onComplete();
            // console.log("API Response:", response.data);
        } catch (error) {
            console.error("API Error:", error);
        } finally {
            setIsPageLoading(false);
        }
    };

    return (
        <>
            {isPageLoading && <PageLoader />}
            <div>
                <p>Upload Media</p>
                <div>
                    <DragDrop
                        onFilesUploaded={handleFileUpload}
                        acceptedFileTypes={{
                            "image/*": [".jpeg", ".png", ".jpg"],
                        }}
                        maxFiles={10}
                        dragDropText="Drag and drop images here"
                    />
                </div>
                <div>
                    {media.length > 0 && (
                        <div>
                            <div className="flex gap-2 w-[95%] flex-wrap justify-start">
                                {media.map((file, index) => (
                                    <div
                                        key={index}
                                        className="w-[158px] h-[142px] rounded-lg border-[1.5px] border-[#BFC4CC] overflow-hidden"
                                    >
                                        {file.loading ? (
                                            <ImageLoader
                                                width="100%"
                                                height="142px"
                                            />
                                        ) : (
                                            <>
                                                <img
                                                    src={file.url}
                                                    alt={file.key}
                                                    className={`w-full bg-cover h-[110px] ${
                                                        !file.loading
                                                            ? "block"
                                                            : "hidden"
                                                    }`}
                                                    onLoad={() =>
                                                        setMedia((prevMedia) =>
                                                            prevMedia.map(
                                                                (mediaEntry) =>
                                                                    mediaEntry.key ===
                                                                    file.key
                                                                        ? {
                                                                              ...mediaEntry,
                                                                              loading: false,
                                                                              error: false,
                                                                          }
                                                                        : mediaEntry,
                                                            ),
                                                        )
                                                    }
                                                    onError={() =>
                                                        setMedia((prevMedia) =>
                                                            prevMedia.map(
                                                                (mediaEntry) =>
                                                                    mediaEntry.key ===
                                                                    file.key
                                                                        ? {
                                                                              ...mediaEntry,
                                                                              loading: false,
                                                                              error: true,
                                                                          }
                                                                        : mediaEntry,
                                                            ),
                                                        )
                                                    }
                                                />
                                                <div className="flex justify-between items-center h-[32px] px-1 bg-[#EEF0FB]">
                                                    <p>
                                                        {trimImgName(file.key)}
                                                    </p>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            className="bg-white rounded-md p-1 border-[1.5px] border-[#F8D9DE] text-[#DC4662] h-[24px] w-[24px]"
                                                            onClick={() =>
                                                                removeImage(
                                                                    index,
                                                                )
                                                            }
                                                        >
                                                            <Trash size={14} />
                                                        </button>
                                                    </div>
                                                </div>{" "}
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <button
                    className="mt-6 px-4 py-2 bg-[#0B22B2] text-white rounded-md absolute bottom-3 left-[19%]"
                    onClick={handleSubmit}
                >
                    Next
                </button>
            </div>
        </>
    );
};

export default MediaGallery;
